import { useEffect, useState } from "react";
import { usePlatform } from "../platform/context";
import { docCirrusRpcClient } from "./rpc/doc-cirrus.client";
import { frontdeskDiagnosticLogger } from "./diagnostic-logger";
import { reportOperationalError } from "./operational-error-telemetry";

interface KimInfo {
  kimAddress: string;
  practiceName: string;
}

/**
 * Hook to get KIM address with local override support
 *
 * Priority:
 * 1. Local settings override (kimAddressOverride)
 * 2. Bridge API (getKimAccount)
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * const { kimInfo, isLoading } = useKimAddress()
 * if (kimInfo) {
 *   return <KimAddressQrCode kimAddress={kimInfo.kimAddress} />
 * }
 * ```
 */
export function useKimAddress() {
  const platform = usePlatform();
  const [kimInfo, setKimInfo] = useState<KimInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(
    function runLoadKimAddressEffect() {
      const loadKimAddress = async () => {
        setIsLoading(true);
        setError(null);

        try {
          // First check for local override
          const settings = await platform.getPracticeSettings();

          // A stored override only steers patient data on a surface whose
          // heartbeat tells SOL it is using one. Elsewhere the practice could
          // not see that Online-Check-in data was being diverted, so the
          // server-configured practice address wins.
          if (
            settings.kimAddressOverride &&
            platform.reportsDeviceStateToSol === true
          ) {
            // Use override with practice name from settings or default
            setKimInfo({
              kimAddress: settings.kimAddressOverride,
              practiceName: settings.practiceName || "Praxis",
            });
            setIsLoading(false);
            return;
          }

          // No override - fetch from bridge, enrich practice name from settings
          const bridgeInfo = await docCirrusRpcClient.frontdesk.getKimAccount();
          if (bridgeInfo) {
            setKimInfo({
              kimAddress: bridgeInfo.kimAddress,
              practiceName: settings.practiceName || bridgeInfo.practiceName,
            });
          }
        } catch (err) {
          frontdeskDiagnosticLogger.error("Failed to load KIM address", {
            error: err,
          });
          void reportOperationalError({
            action: "kim-address.load",
            severity: "error",
            source: "lifecycle",
            component: "useKimAddress",
            error: err,
          });
          setError(
            err instanceof Error
              ? err
              : new Error("Failed to load KIM address"),
          );
        } finally {
          setIsLoading(false);
        }
      };

      loadKimAddress();
    },
    [platform],
  );

  return { kimInfo, isLoading, error };
}
